import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import ChangeLogTable from "./ChangeLogTable";
import Loader from "../../components/Loader";
import useGetTrialForPatient from "../../hooks/useGetTrialForPatient";
import useGetChangeLogs from "../../hooks/useGetChangeLogs";
import useGetChangeLogsFile from "../../hooks/useGetChangeLogsFile";

const ChangeLogs = () => {
  const convertFirstLetterToCapital = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const { _id_ } = useParams();
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data?.role;

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();
  setActive("changeLogs");
  setBreadcrumbTitle("eCRF Change Logs");

  const [trialId, setTrialId] = useState(0);
  const [pageItemCount] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);

  const { data: trialData } = useGetTrialForPatient({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    if (trialData?.trial?.id) {
      setTrialId(trialData.trial.id);
    }
  }, [trialData]);

  const { data: changeLog, isLoading: isLoadingChangeLogs } = useGetChangeLogs({
    page: currentPage + 1,
    trialId,
    patientId: _id_,
    enabled: trialId > 0,
  });

  const {
    data: changeLogFileData,
    refetch: fetchChangeLogFile,
    isFetching: isExporting,
  } = useGetChangeLogsFile({
    trialId,
    patientId: _id_,
    enabled: false,
  });

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetchChangeLogFile();

      const fileUrl = res?.data?.file_url;
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (err) {
      console.error("Failed to export change logs", err);
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {isLoadingChangeLogs ? (
        <Box display="flex" justifyContent="center" py={6}>
          <Loader />
        </Box>
      ) : (
        <>
          {/* Header Actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">eCRF Change Logs</Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleExportCSV}
                disabled={isExporting || !trialId}
              >
                {isExporting ? "Exporting..." : "Export CSV"}
              </Button>

              <Button variant="outlined" size="small" onClick={() => setFullScreenOpen(true)}>
                Full Screen View
              </Button>
            </Stack>
          </Stack>

          {/* Normal View */}
          <ChangeLogTable
            changeLog={changeLog}
            convertFirstLetterToCapital={convertFirstLetterToCapital}
            role={role}
            handlePageClick={handlePageClick}
            pageItemCount={pageItemCount}
            currentPage={currentPage}
          />

          {/* Full Screen Dialog */}
          <Dialog open={fullScreenOpen} onClose={() => setFullScreenOpen(false)} fullScreen>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">eCRF Change Logs – Full Screen</Typography>
              <IconButton onClick={() => setFullScreenOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
              <ChangeLogTable
                changeLog={changeLog}
                convertFirstLetterToCapital={convertFirstLetterToCapital}
                role={role}
                handlePageClick={handlePageClick}
                pageItemCount={pageItemCount}
                currentPage={currentPage}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Paper>
  );
};

export default ChangeLogs;
